// Monetization — the RevenueCat-facing allowance economy catalog (PX-149,
// on top of the PX-148 backend). Three tabs:
//
//   - Subscription products: per-SKU token allowance + trial allowance,
//     mapped to a plan (the "product → allowance" half of the two-layer
//     resolve; "entitlement → plan" lives on the Catalog page).
//   - Credit packs: consumable token packs (non-expiring purchased
//     balance).
//   - Signup bonuses: per-provider grant on registration (free=0 lives
//     here). Fixed provider set — list + patch only, no create/delete.
//
// All three reuse plans.read / plans.write server-side, so the whole page
// is gated on plans.read (route + nav). Same inline-edit + create-form
// chrome as Catalog, sharing FormControls.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { APIError, request } from '@/lib/api'
import { PlanPicker } from '@/components/PlanPicker'
import {
  Checkbox,
  DeleteButton,
  ErrorBanner,
  Field,
  FormActions,
  InlineNumberCell,
  InlineTextCell,
  NewItemButton,
  TabSwitcher,
  Toggle,
  firstApiError,
} from '@/components/FormControls'
import type {
  AdminCreditPackCreate,
  AdminCreditPackResponse,
  AdminCreditPackUpdate,
  AdminPlanResponse,
  AdminSignupBonusResponse,
  AdminSignupBonusUpdate,
  AdminSubscriptionProductCreate,
  AdminSubscriptionProductResponse,
  AdminSubscriptionProductUpdate,
} from '@/types/api'

type Tab = 'products' | 'packs' | 'bonuses'

const TABS: { id: Tab; label: string }[] = [
  { id: 'products', label: 'Subscription products' },
  { id: 'packs', label: 'Credit packs' },
  { id: 'bonuses', label: 'Signup bonuses' },
]

export function MonetizationPage() {
  const [tab, setTab] = useState<Tab>('products')
  return (
    <div className="p-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">Monetization</h1>
        <p className="text-sm text-ink-400">
          RevenueCat product → allowance mapping, token packs, and signup grants.
        </p>
      </header>

      <TabSwitcher tabs={TABS} current={tab} onChange={setTab} />

      {tab === 'products' && <ProductsTab />}
      {tab === 'packs' && <PacksTab />}
      {tab === 'bonuses' && <BonusesTab />}
    </div>
  )
}

// ----- Subscription products ----------------------------------------

function ProductsTab() {
  const queryClient = useQueryClient()
  const key = ['admin', 'catalog', 'subscription-products']
  const productsQuery = useQuery({
    queryKey: key,
    queryFn: () => request<AdminSubscriptionProductResponse[]>('/admin/catalog/subscription-products'),
  })
  // Plans for the plan_id picker. Shared cache key with the Catalog page.
  const plansQuery = useQuery({
    queryKey: ['admin', 'catalog', 'plans'],
    queryFn: () => request<AdminPlanResponse[]>('/admin/catalog/plans'),
  })
  const plans = plansQuery.data ?? []

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: AdminSubscriptionProductUpdate }) =>
      request<AdminSubscriptionProductResponse>(`/admin/catalog/subscription-products/${id}`, { method: 'PATCH', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  })
  const create = useMutation({
    mutationFn: (body: AdminSubscriptionProductCreate) =>
      request<AdminSubscriptionProductResponse>('/admin/catalog/subscription-products', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  })
  const remove = useMutation({
    mutationFn: (id: number) => request(`/admin/catalog/subscription-products/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  })

  const [creating, setCreating] = useState(false)
  const rowError = firstApiError(patch.error, remove.error)

  return (
    <div>
      {rowError && <ErrorBanner error={rowError} />}

      <NewItemButton label="New product" onClick={() => setCreating(true)} />

      {creating && (
        <ProductForm
          plans={plans}
          error={firstApiError(create.error)}
          submitting={create.isPending}
          onSubmit={(body) => create.mutate(body, { onSuccess: () => setCreating(false) })}
          onCancel={() => { create.reset(); setCreating(false) }}
        />
      )}

      <div className="overflow-x-auto rounded-xl border border-ink-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-ink-100 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-400">
            <tr>
              <th className="px-4 py-3">RC product id</th>
              <th className="px-4 py-3">Plan</th>
              <th className="px-4 py-3" title="Tokens granted per period">Allowance</th>
              <th className="px-4 py-3" title="Trial allowance (blank = no trial)">Trial</th>
              <th className="px-4 py-3">Period</th>
              <th className="px-4 py-3">Active</th>
              <th className="px-4 py-3" title="Show a 'Most Popular' highlight on this SKU in the iOS paywall">Popular</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {productsQuery.isLoading && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-ink-400">Loading…</td></tr>
            )}
            {!productsQuery.isLoading && productsQuery.data?.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-ink-400">No subscription products yet.</td></tr>
            )}
            {productsQuery.data?.map((p) => (
              <tr key={p.id} className="border-b border-ink-100 last:border-0">
                <td className="px-4 py-3 font-mono text-xs">
                  <InlineTextCell
                    value={p.revenuecat_product_id}
                    mono
                    disabled={patch.isPending}
                    onCommit={(v) => patch.mutate({ id: p.id, body: { revenuecat_product_id: v } })}
                  />
                </td>
                <td className="px-4 py-3">
                  <PlanPicker
                    value={p.plan_id}
                    fallbackSlug={`#${p.plan_id}`}
                    plans={plans}
                    disabled={patch.isPending || plansQuery.isLoading}
                    onChange={(plan_id) => patch.mutate({ id: p.id, body: { plan_id } })}
                  />
                </td>
                <td className="px-4 py-3 tabular-nums">
                  <InlineNumberCell
                    value={p.token_allowance}
                    min={0}
                    widthClass="w-20"
                    disabled={patch.isPending}
                    onCommit={(v) => { patch.mutate({ id: p.id, body: { token_allowance: v } }) }}
                  />
                </td>
                <td className="px-4 py-3 tabular-nums">
                  <InlineTextCell
                    value={p.trial_allowance == null ? '' : String(p.trial_allowance)}
                    allowEmpty
                    placeholder="—"
                    disabled={patch.isPending}
                    onCommit={(v) => {
                      if (v === '') { patch.mutate({ id: p.id, body: { trial_allowance: null } }); return }
                      const n = Number(v)
                      if (Number.isInteger(n) && n >= 0) patch.mutate({ id: p.id, body: { trial_allowance: n } })
                    }}
                  />
                </td>
                <td className="px-4 py-3">
                  <InlineTextCell
                    value={p.period}
                    disabled={patch.isPending}
                    onCommit={(v) => patch.mutate({ id: p.id, body: { period: v } })}
                  />
                </td>
                <td className="px-4 py-3">
                  <Toggle
                    checked={p.is_active}
                    disabled={patch.isPending}
                    onChange={(v) => patch.mutate({ id: p.id, body: { is_active: v } })}
                  />
                </td>
                <td className="px-4 py-3">
                  <Toggle
                    checked={p.most_popular}
                    disabled={patch.isPending}
                    onChange={(v) => patch.mutate({ id: p.id, body: { most_popular: v } })}
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <DeleteButton
                    disabled={remove.isPending}
                    confirmText={`Delete subscription product "${p.revenuecat_product_id}"?`}
                    onConfirm={() => remove.mutate(p.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ProductForm({
  plans, onSubmit, onCancel, submitting, error,
}: {
  plans: AdminPlanResponse[]
  onSubmit: (body: AdminSubscriptionProductCreate) => void
  onCancel: () => void
  submitting: boolean
  error: APIError | null
}) {
  const [body, setBody] = useState<AdminSubscriptionProductCreate>({
    revenuecat_product_id: '',
    plan_id: plans[0]?.id ?? 1,
    token_allowance: 0,
    trial_allowance: null,
    period: 'month',
    is_active: true,
    most_popular: false,
    sort_order: 0,
  })
  const [trialDraft, setTrialDraft] = useState('')

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        const trial = trialDraft.trim() === '' ? null : Number(trialDraft)
        onSubmit({ ...body, trial_allowance: trial != null && Number.isFinite(trial) ? trial : null })
      }}
      className="mb-4 rounded-xl border border-ink-100 bg-white p-4 shadow-sm"
    >
      {error && <ErrorBanner error={error} />}
      <div className="grid grid-cols-2 gap-3">
        <Field label="RC product id" value={body.revenuecat_product_id} onChange={(v) => setBody({ ...body, revenuecat_product_id: v })} mono />
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium text-ink-500">Plan</span>
          <div>
            <PlanPicker
              value={body.plan_id}
              fallbackSlug={`#${body.plan_id}`}
              plans={plans}
              onChange={(plan_id) => setBody({ ...body, plan_id })}
            />
          </div>
        </label>
        <Field label="Token allowance" type="number" value={String(body.token_allowance)} onChange={(v) => setBody({ ...body, token_allowance: Number(v) || 0 })} mono />
        <Field label="Trial allowance (blank = none)" type="number" value={trialDraft} onChange={setTrialDraft} mono />
        <Field label="Period" value={body.period} onChange={(v) => setBody({ ...body, period: v })} placeholder="week / month / quarter" />
        <Field label="Sort order" type="number" value={String(body.sort_order ?? 0)} onChange={(v) => setBody({ ...body, sort_order: Number(v) || 0 })} mono />
        <Checkbox label="Active" checked={Boolean(body.is_active)} onChange={(v) => setBody({ ...body, is_active: v })} />
        <Checkbox label="Most popular" checked={Boolean(body.most_popular)} onChange={(v) => setBody({ ...body, most_popular: v })} />
      </div>
      <FormActions onCancel={onCancel} submitting={submitting} />
    </form>
  )
}

// ----- Credit packs --------------------------------------------------

function PacksTab() {
  const queryClient = useQueryClient()
  const key = ['admin', 'catalog', 'credit-packs']
  const packsQuery = useQuery({
    queryKey: key,
    queryFn: () => request<AdminCreditPackResponse[]>('/admin/catalog/credit-packs'),
  })

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: AdminCreditPackUpdate }) =>
      request<AdminCreditPackResponse>(`/admin/catalog/credit-packs/${id}`, { method: 'PATCH', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  })
  const create = useMutation({
    mutationFn: (body: AdminCreditPackCreate) =>
      request<AdminCreditPackResponse>('/admin/catalog/credit-packs', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  })
  const remove = useMutation({
    mutationFn: (id: number) => request(`/admin/catalog/credit-packs/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  })

  const [creating, setCreating] = useState(false)
  const rowError = firstApiError(patch.error, remove.error)

  return (
    <div>
      {rowError && <ErrorBanner error={rowError} />}

      <NewItemButton label="New pack" onClick={() => setCreating(true)} />

      {creating && (
        <PackForm
          error={firstApiError(create.error)}
          submitting={create.isPending}
          onSubmit={(body) => create.mutate(body, { onSuccess: () => setCreating(false) })}
          onCancel={() => { create.reset(); setCreating(false) }}
        />
      )}

      <div className="overflow-x-auto rounded-xl border border-ink-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-ink-100 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-400">
            <tr>
              <th className="px-4 py-3">Slug</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">RC product id</th>
              <th className="px-4 py-3" title="Tokens granted on purchase">Credits</th>
              <th className="px-4 py-3">Active</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {packsQuery.isLoading && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-ink-400">Loading…</td></tr>
            )}
            {!packsQuery.isLoading && packsQuery.data?.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-ink-400">No credit packs yet.</td></tr>
            )}
            {packsQuery.data?.map((p) => (
              <tr key={p.id} className="border-b border-ink-100 last:border-0">
                <td className="px-4 py-3 font-mono text-xs">
                  <InlineTextCell value={p.slug} mono disabled={patch.isPending} onCommit={(v) => patch.mutate({ id: p.id, body: { slug: v } })} />
                </td>
                <td className="px-4 py-3 font-medium text-ink-900">
                  <InlineTextCell value={p.name} disabled={patch.isPending} onCommit={(v) => patch.mutate({ id: p.id, body: { name: v } })} />
                </td>
                <td className="px-4 py-3 font-mono text-xs">
                  <InlineTextCell value={p.revenuecat_product_id} mono disabled={patch.isPending} onCommit={(v) => patch.mutate({ id: p.id, body: { revenuecat_product_id: v } })} />
                </td>
                <td className="px-4 py-3 tabular-nums">
                  <InlineNumberCell value={p.credits} min={0} widthClass="w-20" disabled={patch.isPending} onCommit={(v) => patch.mutate({ id: p.id, body: { credits: v } })} />
                </td>
                <td className="px-4 py-3">
                  <Toggle checked={p.is_active} disabled={patch.isPending} onChange={(v) => patch.mutate({ id: p.id, body: { is_active: v } })} />
                </td>
                <td className="px-4 py-3 text-right">
                  <DeleteButton
                    disabled={remove.isPending}
                    confirmText={`Delete credit pack "${p.name}"?`}
                    onConfirm={() => remove.mutate(p.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function PackForm({
  onSubmit, onCancel, submitting, error,
}: {
  onSubmit: (body: AdminCreditPackCreate) => void
  onCancel: () => void
  submitting: boolean
  error: APIError | null
}) {
  const [body, setBody] = useState<AdminCreditPackCreate>({
    slug: '',
    name: '',
    revenuecat_product_id: '',
    credits: 0,
    is_active: true,
    sort_order: 0,
  })

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(body) }}
      className="mb-4 rounded-xl border border-ink-100 bg-white p-4 shadow-sm"
    >
      {error && <ErrorBanner error={error} />}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Slug" value={body.slug} onChange={(v) => setBody({ ...body, slug: v })} mono />
        <Field label="Name" value={body.name} onChange={(v) => setBody({ ...body, name: v })} />
        <Field label="RC product id" value={body.revenuecat_product_id} onChange={(v) => setBody({ ...body, revenuecat_product_id: v })} mono />
        <Field label="Credits" type="number" value={String(body.credits)} onChange={(v) => setBody({ ...body, credits: Number(v) || 0 })} mono />
        <Field label="Sort order" type="number" value={String(body.sort_order ?? 0)} onChange={(v) => setBody({ ...body, sort_order: Number(v) || 0 })} mono />
        <Checkbox label="Active" checked={Boolean(body.is_active)} onChange={(v) => setBody({ ...body, is_active: v })} />
      </div>
      <FormActions onCancel={onCancel} submitting={submitting} />
    </form>
  )
}

// ----- Signup bonuses (list + patch only) ----------------------------

function BonusesTab() {
  const queryClient = useQueryClient()
  const key = ['admin', 'catalog', 'signup-bonuses']
  const bonusesQuery = useQuery({
    queryKey: key,
    queryFn: () => request<AdminSignupBonusResponse[]>('/admin/catalog/signup-bonuses'),
  })

  const patch = useMutation({
    mutationFn: ({ provider, body }: { provider: string; body: AdminSignupBonusUpdate }) =>
      request<AdminSignupBonusResponse>(`/admin/catalog/signup-bonuses/${provider}`, { method: 'PATCH', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  })
  const rowError = firstApiError(patch.error)

  return (
    <div>
      {rowError && <ErrorBanner error={rowError} />}
      <p className="mb-3 text-sm text-ink-400">
        Fixed provider set — credits granted on first registration. Set <code className="rounded bg-ink-100 px-1 py-0.5 text-xs">credits = 0</code> to disable the grant for a provider.
      </p>

      <div className="overflow-x-auto rounded-xl border border-ink-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-ink-100 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-400">
            <tr>
              <th className="px-4 py-3">Provider</th>
              <th className="px-4 py-3" title="Tokens granted on signup">Credits</th>
              <th className="px-4 py-3">Enabled</th>
              <th className="px-4 py-3">Updated</th>
            </tr>
          </thead>
          <tbody>
            {bonusesQuery.isLoading && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-ink-400">Loading…</td></tr>
            )}
            {bonusesQuery.data?.map((b) => (
              <tr key={b.auth_provider} className="border-b border-ink-100 last:border-0">
                <td className="px-4 py-3 font-mono text-xs">{b.auth_provider}</td>
                <td className="px-4 py-3 tabular-nums">
                  <InlineNumberCell
                    value={b.credits}
                    min={0}
                    widthClass="w-20"
                    disabled={patch.isPending}
                    onCommit={(v) => patch.mutate({ provider: b.auth_provider, body: { credits: v } })}
                  />
                </td>
                <td className="px-4 py-3">
                  <Toggle
                    checked={b.enabled}
                    disabled={patch.isPending}
                    onChange={(v) => patch.mutate({ provider: b.auth_provider, body: { enabled: v } })}
                  />
                </td>
                <td className="px-4 py-3 text-xs text-ink-500">{new Date(b.updated_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

